import React, { useEffect, useState } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useReservation } from '../components/ReservationContext';
import { Button, Modal, Form, Row, Col } from 'react-bootstrap';
import dayjs from 'dayjs';
import CalendarGrid from '../components/CalendarGrid';
import '../css/Step1.css';

export default function Step1_Dates({ setStepNavigationProps }) {
  const { reservation, setReservation } = useReservation();
  const navigate = useNavigate();

  const [selectedRange, setSelectedRange] = useState({
    from: reservation.checkIn || null,
    to: reservation.checkOut || null,
  });
  const [adults, setAdults] = useState(reservation.adults || 2);
  const [children, setChildren] = useState(reservation.children || 0);

  const [errors, setErrors] = useState({});
  const [showCalendar, setShowCalendar] = useState(false);

  // calendarStartMonth is the initial month we give to CalendarGrid when opening it.
  // We intentionally DO NOT reset this on "Clear" so the calendar stays on the same month.
  const [calendarStartMonth, setCalendarStartMonth] = useState(new Date());

  // Update reservation context when a full range is selected OR when cleared
  useEffect(() => {
    if (selectedRange.from && selectedRange.to) {

      const nights = dayjs(selectedRange.to).diff(dayjs(selectedRange.from), 'day');

      setReservation(prev => ({
        ...prev,
        checkIn: selectedRange.from,
        checkOut: selectedRange.to,
        nights,
        adults,
        children,
      }));
    } else {
      // If there's not a full range, clear checkIn/checkOut in context
      setReservation(prev => ({
        ...prev,
        checkIn: null,
        checkOut: null,
        nights: 0,
        adults,
        children,
      }));
    }
  }, [selectedRange, adults, children, setReservation]);

  // Step navigation
  useEffect(() => {
    setStepNavigationProps({
      onContinue: handleContinue,
      disableContinue: !selectedRange.from || !selectedRange.to || !adults,
      hideBack: true,
    });

    return () => setStepNavigationProps({});
  }, [selectedRange, adults, children]);

  const handleContinue = () => {
    const newErrors = {};
    if (!selectedRange.from) newErrors.checkIn = 'Please select check-in date';
    if (!selectedRange.to) newErrors.checkOut = 'Please select check-out date';
    if (!adults || adults === 0) newErrors.adults = 'Please enter at least 1 adult';

    if (Object.keys(newErrors).length > 0) return setErrors(newErrors);

    navigate('/room');
  };

  return (
    <div className="p-3" style={{ maxWidth: 500, margin: 'auto' }}>
      <div className="mb-4">
        <h2 className="h5 fw-semibold text-dark mb-3">Let’s get started!</h2>
        <p className="text-muted mb-2">
          Let us know your travel dates and how many guests will be joining you.
        </p>
      </div>

      {/* Calendar button */}
      <div
        className="date-picker-button w-100 mb-3 d-flex justify-content-between align-items-center px-3 py-2 border rounded"
        style={{ cursor: 'pointer', backgroundColor: '#fff', border: '1px solid #ced4da' }}
        onClick={() => {
          // When opening, prefer to show the month of the existing selection (if any).
          // We keep calendarStartMonth unchanged on Clear so the modal will show the last tracked month.
          if (selectedRange.from) {
            setCalendarStartMonth(new Date(selectedRange.from));
          } else {
            // If no selection and calendarStartMonth already points to a previously viewed month,
            // keep it — otherwise default to today.
            if (!calendarStartMonth) setCalendarStartMonth(new Date());
          }
          setShowCalendar(true);
        }}
      >
        {!selectedRange.from && !selectedRange.to && (
          <span className="text-muted">Click to choose your dates</span>
        )}

        {selectedRange.from && selectedRange.to && (
          <span className="fw-semibold">
            {dayjs(selectedRange.from).format('MMM D')} – {dayjs(selectedRange.to).format('MMM D, YYYY')}
          </span>
        )}

        {selectedRange.from && !selectedRange.to && (
          <span className="fw-semibold">
            {dayjs(selectedRange.from).format('MMM D')} –{' '}
            <span style={{ color: 'red' }}>Select check-out date</span>
          </span>
        )}

<i
  className="bi bi-calendar-event ms-2"
  style={{ color: "#45906dff" }}
></i>
      </div>

      {errors.checkIn && <div className="text-danger small">{errors.checkIn}</div>}
      {errors.checkOut && <div className="text-danger small">{errors.checkOut}</div>}

      {/* Guest selection */}
   <Row className="mb-4 mt-3 g-2">
  <Col xs={6}>
    <Form.Group controlId="adults" className="mb-2">
      <Form.Label>Adults</Form.Label>
      <Form.Select
        value={adults}
        onChange={e => setAdults(Number(e.target.value))}
      >
        {[...Array(25)].map((_, i) => (
          <option key={i + 1} value={i + 1}>
            {i + 1}
          </option>
        ))}
      </Form.Select>

      {errors.adults && (
        <div className="text-danger small mt-1">{errors.adults}</div>
      )}
    </Form.Group>
  </Col>

  <Col xs={6}>
    <Form.Group controlId="children" className="mb-2">
      <Form.Label>Children (0–18 age)</Form.Label>
      <Form.Select
        value={children}
        onChange={e => setChildren(Number(e.target.value))}
      >
        {[...Array(20)].map((_, i) => (
          <option key={i} value={i}>
            {i}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  </Col>
</Row>


      {/* Calendar modal */}
      <Modal
        show={showCalendar}
        onHide={() => {
          setShowCalendar(false);
        }}
        centered
        backdrop="static"
        contentClassName="calendar-modal-content"
      >
        <Modal.Header closeButton>
          <Modal.Title>Choose Your Dates</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-2 d-flex flex-column align-items-center">
          <div className="mt-4 text-center fw-bold" style={{ color: '#45906dff' }}>
            {!selectedRange.from && <span>Select check-in date to begin</span>}
            {selectedRange.from && !selectedRange.to && <span>Now select your check-out date</span>}
            {selectedRange.from && selectedRange.to && (
              <span>
                {dayjs(selectedRange.from).format('MMM D')} to {dayjs(selectedRange.to).format('MMM D, YYYY')} (
                {dayjs(selectedRange.to).diff(dayjs(selectedRange.from), 'day')} nights)
              </span>
            )}
          </div>

          <CalendarGrid
            month={calendarStartMonth}
            selectedRange={selectedRange}
            // Optional: CalendarGrid can call this with the currently visible month whenever it changes.
            // If you implement onMonthChange inside CalendarGrid, parent will always know last viewed month.
            onMonthChange={(m) => {
              // keep track of last viewed month (optional; CalendarGrid must call this)
              if (m) setCalendarStartMonth(m);
            }}
            onSelectRange={(range) => {
              if (range.from && range.to && range.from === range.to) {
                setSelectedRange({ from: range.from, to: null });
              } else {
                setSelectedRange(range);
              }

              if (range.from && range.to && range.from !== range.to) {
                setShowCalendar(false);
              }
            }}
          />

          <div className="mt-3 d-flex justify-content-center">
            <Button
              size="sm"
              style={{
                backgroundColor: '#fff',
                color: '#000',
                borderColor: '#000',
                fontWeight: 'bold',
              }}
              onClick={() => {
                // CLEAR selection and reservation context
                setSelectedRange({ from: null, to: null });
                setErrors({});
                setReservation(prev => ({
                  ...prev,
                  checkIn: null,
                  checkOut: null,
                  nights: 0
                  
                }));

                // IMPORTANT: do NOT reset calendarStartMonth — keep the same visible month
                // so user remains on the month they were viewing.
              }}
            >
              Clear Date Selection
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
